// src/components/routing/FieldOpsAiPanel.js
// --- UPDATE (Field Ops · Zones v1) — NEW FILE ---------------------------------------------
// FlowSense AI for Field Operations: operations health score, ranked findings (pressure, no
// cover, demand spikes [z-score], workload outliers, rising forecast [Holt], zones without an
// active rule, jobs outside every zone, staff without GPS) — each with an action — plus the AI
// dispatch suggestions (Assign) and "Ask FlowSense AI" (Ollama, module field_operations; the
// built-in rules answer when the engine is offline). "Explain Field Operations" describes how
// zones / constituencies, rules, teams, skills and dispatch fit together, with live numbers.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, Icon, Input, IconButton, Spinner, Wrap, WrapItem, CircularProgress, CircularProgressLabel,
    Tooltip, Skeleton, Tabs, TabList, Tab, TabPanels, TabPanel, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdSend, MdArrowForward, MdBolt, MdSchool } from 'react-icons/md';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { answerFieldOpsLocally, buildFieldOpsAiContext, explainOperations, FIELD_OPS_QUESTIONS } from './fieldOpsAiIntel';

const LEVEL = { critical: 'red', high: 'orange', medium: 'yellow', info: 'blue' };

function FieldOpsAiPanel({ data, isLoading, selectedZoneId, onAction, onAssign, assigning }) {
    const [q, setQ] = useState('');
    const [history, setHistory] = useState([]);
    const [asking, setAsking] = useState(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const bg = useColorModeValue('linear(135deg, blue.50, white 55%, purple.50)', 'linear(135deg, rgba(49,130,206,0.14), gray.800 55%, rgba(128,90,213,0.14))');
    const border = useColorModeValue('blue.100', 'whiteAlpha.200');
    const tile = useColorModeValue('white', 'gray.800');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const ask = useCallback(async (text) => {
        const question = String(text || '').trim();
        if (!question || asking) return;
        setQ(''); setAsking(true);
        let entry;
        try {
            const r = await askFlowSenseAi(question, buildFieldOpsAiContext(data, selectedZoneId), 'field_operations');
            if (!r?.answer) throw new Error('empty');
            entry = { q: question, answer: r.answer, source: 'ollama', model: r.model };
        } catch (_) {
            entry = { q: question, ...answerFieldOpsLocally(question, data) };
        }
        if (mounted.current) { setHistory((h) => [{ ...entry, at: Date.now() + Math.random() }, ...h].slice(0, 6)); setAsking(false); }
    }, [asking, data, selectedZoneId]);

    const explain = useCallback(() => setHistory((h) => [{ q: 'Explain Field Operations', answer: explainOperations(data), source: 'rules', at: Date.now() + Math.random() }, ...h].slice(0, 6)), [data]);

    const score = data?.score;
    const color = score == null ? 'gray' : score >= 85 ? 'green' : score >= 60 ? 'yellow' : 'red';
    const findings = data?.findings || [];
    const dispatch = data?.recommendations?.dispatch || [];
    const rebalance = data?.recommendations?.rebalance || [];

    return (
        <Box bgGradient={bg} borderWidth="1px" borderColor={border} borderRadius="2xl" p={4} role="region" aria-label="FlowSense AI field operations">
            <Flex align="center" gap={3} mb={3} wrap="wrap">
                <CircularProgress value={score ?? 0} color={`${color}.400`} size="52px" thickness="10px" isIndeterminate={isLoading && !data}>
                    <CircularProgressLabel fontSize="sm" fontWeight="900">{score ?? '—'}</CircularProgressLabel>
                </CircularProgress>
                <Box flex={1} minW="180px">
                    <HStack spacing={2}><Icon as={MdAutoAwesome} color="purple.400" /><Text fontWeight="900">FlowSense AI · operations</Text></HStack>
                    <Text fontSize="xs" color={muted}>
                        {data ? `${data.totals.zones} zones · ${data.totals.open} open jobs · ${data.totals.technicians_online}/${data.totals.technicians} staff online` : 'Analysing zones, work and crews…'}
                    </Text>
                </Box>
                <Button size="xs" variant="outline" colorScheme="purple" leftIcon={<Icon as={MdSchool} />} onClick={explain}>Explain Field Operations</Button>
            </Flex>

            <Tabs size="sm" variant="soft-rounded" colorScheme="purple" isLazy>
                <TabList mb={2}>
                    <Tab>Findings ({findings.length})</Tab>
                    <Tab>Dispatch ({dispatch.length + rebalance.length})</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel p={0}>
                        {isLoading && !data ? <Skeleton h="80px" borderRadius="lg" /> : !findings.length ? (
                            <Text fontSize="sm" color={muted} mb={2}>Nothing needs attention — every zone is covered.</Text>
                        ) : (
                            <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto" pr={1}>
                                {findings.slice(0, 10).map((f) => (
                                    <Flex key={f.id} bg={tile} p={2.5} borderRadius="lg" borderLeftWidth="4px" borderLeftColor={`${LEVEL[f.level] || 'gray'}.400`} gap={3} align="center">
                                        <Box flex={1} minW={0}>
                                            <HStack spacing={2} flexWrap="wrap">
                                                <Badge colorScheme={LEVEL[f.level]} fontSize="2xs">{f.level}</Badge>
                                                <Text fontSize="sm" fontWeight="700">{f.title}</Text>
                                                {f.ml && <Tooltip hasArrow label={`Machine-learning signal: ${f.ml}`}><Badge colorScheme="purple" fontSize="2xs" cursor="help">ML</Badge></Tooltip>}
                                            </HStack>
                                            <Text fontSize="xs" color={muted} mt={0.5}>{f.detail}</Text>
                                        </Box>
                                        {f.action && onAction && (
                                            <Button size="xs" rightIcon={<Icon as={MdArrowForward} />} onClick={() => onAction(f.action)} flexShrink={0}>
                                                {f.action.type === 'zone' ? 'Show zone' : f.action.type === 'tab' ? 'Open' : 'Show'}
                                            </Button>
                                        )}
                                    </Flex>
                                ))}
                            </VStack>
                        )}
                    </TabPanel>
                    <TabPanel p={0}>
                        {!dispatch.length && !rebalance.length ? <Text fontSize="sm" color={muted}>No suggestions — urgent jobs with a location are covered.</Text> : (
                            <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto" pr={1}>
                                {dispatch.slice(0, 10).map((d) => (
                                    <HStack key={`d-${d.work_order_id}`} bg={tile} p={2.5} borderRadius="lg" align="flex-start">
                                        <Icon as={MdBolt} color="purple.400" mt={1} />
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="sm" fontWeight="700" noOfLines={1}>#{d.work_order_id} {d.title}</Text>
                                            <Text fontSize="xs" color={muted}>→ {d.tech_name} · {d.distance_km} km · ~{d.eta_min} min · {d.tech_open_jobs} open</Text>
                                        </Box>
                                        <Badge colorScheme={d.priority === 'critical' ? 'red' : 'orange'} fontSize="2xs">{d.priority}</Badge>
                                        {onAssign && <Button size="xs" colorScheme="purple" isLoading={assigning === d.work_order_id} onClick={() => onAssign(d)}>Assign</Button>}
                                    </HStack>
                                ))}
                                {rebalance.map((r) => (
                                    <Box key={`r-${r.tech_id}-${r.to_zone_id}`} bg={tile} p={2.5} borderRadius="lg">
                                        <Text fontSize="sm" fontWeight="700">Move {r.tech_name}: {r.from_zone} → {r.to_zone}</Text>
                                        <Text fontSize="xs" color={muted}>{r.reason} ({r.distance_km} km)</Text>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <Wrap spacing={1} mt={3} mb={2}>
                {FIELD_OPS_QUESTIONS.map((x) => <WrapItem key={x}><Button size="xs" variant="outline" colorScheme="purple" onClick={() => ask(x)} isDisabled={asking}>{x}</Button></WrapItem>)}
            </Wrap>
            <HStack as="form" onSubmit={(e) => { e.preventDefault(); ask(q); }}>
                <Input size="sm" bg={tile} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about a zone, constituency, technician or the operation…" aria-label="Ask FlowSense AI about field operations" maxLength={500} />
                <IconButton type="submit" size="sm" colorScheme="purple" icon={asking ? <Spinner size="xs" /> : <Icon as={MdSend} />} isDisabled={asking || !q.trim()} aria-label="Ask FlowSense" />
            </HStack>
            <VStack align="stretch" spacing={2} mt={3} aria-live="polite">
                {history.map((h) => (
                    <Box key={h.at} bg={tile} p={3} borderRadius="lg">
                        <HStack justify="space-between" mb={1}>
                            <Text fontSize="xs" fontWeight="700" noOfLines={1}>{h.q}</Text>
                            <Badge colorScheme={h.source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">{h.source === 'ollama' ? `FlowSense AI${h.model ? ` · ${h.model}` : ''}` : 'FlowSense rules'}</Badge>
                        </HStack>
                        <Text fontSize="sm" whiteSpace="pre-wrap">{h.answer}</Text>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
}

FieldOpsAiPanel.propTypes = { data: PropTypes.object, isLoading: PropTypes.bool, selectedZoneId: PropTypes.any, onAction: PropTypes.func, onAssign: PropTypes.func, assigning: PropTypes.any };

export default memo(FieldOpsAiPanel);
